import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { railDetailTemplate } from '@xshop/shared';
import type { StorefrontProduct } from '../../api/client';
import { orderable } from '../../commerce/variantStock';
import { useShopWords } from '../../commerce/shopWords';
import { isCaptureVisit } from '../../themeHost/captureScreen';
import { productFactsOf } from '../../themeHost/themeMount';
import { CatalogNote } from './catalogCards';
import { ElementNodeView, ROOT_PATH } from './ElementNode';
import { boolProp, choiceProp, numberProp, textProp } from './elementProps';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { ProductListEmpty } from './ProductListEmpty';
import { ProductScope } from './ProductScope';
import { newestOnRail, railFaces, railTag } from './railGarment';
import { RailDetail } from './RailDetail';
import { responsive } from './responsiveImage';
import { money, resolveBinding } from './storeFacts';
import { useHasMounted } from './useHasMounted';
import { useProductListRows } from './useProductListRows';
import { canHover, useRailCentre, useRailDrift, useRailPress } from './useRailMotion';
import './elementProductRail.css';

// Products hanging on a rail, as both reference stores hang them: side-on and close together, until the pointer reaches
// one, which narrows to an edge and opens to face the shopper while its neighbours make room. Pressing it brings it
// forward over the blurred page, with the elements this one holds under it. Everything shown is the store's own catalogue.

/** How long the pointer may be off the rail before its garment turns back, so crossing a gap never flickers. */
const LEAVE_GRACE_MS = 200;

/** How a rail may end, exactly as `productRailElement` offers it. */
const RAIL_ENDS = ['window', 'caps'] as const;

/** How wide a garment's picture shows at most, for choosing the right size of a store upload. */
const PICTURE_SIZES = '(max-width: 749px) 66vw, 480px';

/** A front picture is fetched after the page's own; React 18 does not know fetchPriority, so it goes on as the lowercase attribute the browser reads. */
const LATER = { fetchpriority: 'low' } as Record<string, string>;

/** The heights and the room a rail keeps to, as `productRailElement` offers them, whatever a hand-written page says. */
const HEIGHTS = [240, 720] as const;
const ROOMS = [20, 70] as const;

// Holds a number inside its range.
const within = (value: number, [least, most]: readonly [number, number]): number => Math.min(most, Math.max(least, value));

/** How many garments' pictures are fetched at once, the ones a window can show before it moves. */
const FIRST_SCREEN = 8;

/** What one garment hangs by: the product, its place, what the rail is doing, and what the merchant asked it to show. */
type GarmentProps = {
  product: StorefrontProduct; at: number; turned: number | null; away: boolean; showTag: boolean; mayBeNew: boolean;
  onTurn: (at: number, slot: HTMLElement) => void; onOpen: (at: number) => void; pictureRef: (element: HTMLElement | null) => void;
};

// Reads the facts of a garment's first option that can be ordered, as its tag and its price under the rail need them.
function useGarmentFacts(product: StorefrontProduct) {
  const variant = product.variants.find((row) => orderable(row)) ?? product.variants[0];
  return productFactsOf(product, { unitPrice: Number(variant?.price ?? 0), compareAtPrice: variant?.compareAtPrice ? Number(variant.compareAtPrice) : null }, product.images);
}

// Says which side of the turned garment one stands on, which is the way it moves to make room.
const sideOf = (at: number, turned: number | null): 'before' | 'after' | undefined => (turned === null || turned === at ? undefined : at < turned ? 'before' : 'after');

// Draws the tag a garment wears, hanging at its collar, or nothing when it has nothing to say.
const GarmentTag: React.FC<{ product: StorefrontProduct; mayBeNew: boolean }> = ({ product, mayBeNew }) => {
  const facts = useGarmentFacts(product);
  const { store } = useElementRender();
  const words = useShopWords();
  if (!facts || product.id.startsWith('sample-')) return null;
  const said = (source: string): string => String(resolveBinding(source, store ? { ...store, product: facts } : null) ?? '');
  const tag = railTag({ ...facts, isNew: mayBeNew && facts.isNew }, { soldOut: said('product.soldOutBadge') || words.soldOut, isNew: said('product.newBadge'), onlyLeft: (count) => words.onlyLeft.replace('{count}', String(count)) });
  return tag ? <span className="xe-rail-tag" data-kind={tag.kind}>{tag.words}</span> : null;
};

// Draws one garment: its side view and its front view stacked on one hook, the button a shopper hovers and presses.
const RailGarment: React.FC<GarmentProps> = ({ product, at, turned, away, showTag, mayBeNew, onTurn, onOpen, pictureRef }) => {
  const faces = railFaces(product, null);
  const live = useHasMounted();
  const side = sideOf(at, turned);
  const near = side !== undefined && turned !== null && Math.abs(at - turned) === 1;
  const edge = !faces.side;
  return (
    <button
      type="button" className="xe-rail-slot" data-rail-slot="" data-turned={turned === at || undefined} data-side={side} data-near={near || undefined} data-away={away || undefined} data-flat={edge || undefined}
      aria-haspopup="dialog" aria-label={product.title} onPointerEnter={(event) => onTurn(at, event.currentTarget)} onFocus={(event) => { if (event.currentTarget.matches(':focus-visible')) onTurn(at, event.currentTarget); }} onClick={() => onOpen(at)}
    >
      <span className="xe-rail-swing" ref={pictureRef}>
        {faces.side ? <img className="xe-rail-face" data-face="side" {...responsive(faces.side, PICTURE_SIZES)} alt="" loading={at < FIRST_SCREEN ? 'eager' : 'lazy'} decoding="async" draggable={false} /> : null}
        {faces.front && (edge || live) ? <img className="xe-rail-face" data-face={edge ? 'turned' : 'front'} {...responsive(faces.front, PICTURE_SIZES)} alt="" loading="eager" {...LATER} decoding="sync" draggable={false} /> : null}
      </span>
      {showTag ? <GarmentTag product={product} mayBeNew={mayBeNew} /> : null}
    </button>
  );
};

// Draws a turned garment's name and price, in the store's money.
const GarmentName: React.FC<{ product: StorefrontProduct }> = ({ product }) => {
  const { store } = useElementRender();
  const facts = useGarmentFacts(product);
  return <p className="xe-rail-caption"><strong>{product.title}</strong>{facts && store ? <span>{money(facts.price, store)}</span> : null}</p>;
};

// Draws what is under the rail: the merchant's own words, until a garment turned and its name and price take their place.
const RailCaption: React.FC<{ product: StorefrontProduct | null; restLine: string }> = ({ product, restLine }) =>
  (product ? <GarmentName key={product.id} product={product} /> : <p className="xe-rail-caption" key="rest">{restLine}</p>);

// Holds which garment faces the shopper and which came forward, turning one back only once the pointer has really left.
function useRailState(count: number, frozen: boolean) {
  const [hovered, setHovered] = useState<number | null>(null);
  const [opened, setOpened] = useState<number | null>(null);
  const leaving = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(leaving.current), []);
  // A picture taken for the themes screen, and the builder, hold a garment turned, so the idea shows while nothing moves.
  const held = frozen || isCaptureVisit() ? Math.floor(count / 2) : null;
  return {
    turned: opened ?? hovered ?? held, opened, setOpened,
    turn: (at: number) => { clearTimeout(leaving.current); setHovered(at); },
    leave: () => { clearTimeout(leaving.current); leaving.current = setTimeout(() => setHovered(null), LEAVE_GRACE_MS); }
  };
}

// Finds what the pressed garment's dialog is drawn into: the store's own scope, whose tokens it reads, outside any band that could clip it.
const dialogHost = (from: HTMLElement | null): Element => from?.closest('.xe-scope') ?? document.body;

// Draws the rail with its garments, the words under it, and the garment that came forward over the page.
export const ProductRailElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing, motionPreview } = useElementRender();
  const frozen = editing && !motionPreview;
  const { rows, read, from } = useProductListRows({ limit: 10, ...node.props });
  const rail = useRailState(rows.length, frozen);
  const view = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const pictures = useRef<Array<HTMLElement | null>>([]);
  const reveal = useRailDrift(view, track, !frozen);
  useRailCentre(view, !frozen && rows.length > 0, rail.turn);
  // The garment facing the shopper is the one a press means, even if the rail has glided a little under a still pointer.
  const press = useRailPress((at) => rail.setOpened(rail.turned ?? at));
  const template = node.children.length ? children : railDetailTemplate(node.id).map((child) => <ElementNodeView key={child.id} node={child} path={ROOT_PATH} />);
  const size = { '--xe-rail-tall': `${within(numberProp(node.props, 'garmentHeight', 460), HEIGHTS)}px`, '--xe-rail-room': String(within(numberProp(node.props, 'garmentRoom', 33), ROOMS) / 100) } as React.CSSProperties;
  const shown = rail.turned === null ? null : rows[rail.turned] ?? null;
  const newest = newestOnRail(rows);
  return (
    <div {...box} className={[box.className, 'xe-rail'].filter(Boolean).join(' ')} data-ends={choiceProp(node.props, 'railEnds', RAIL_ENDS, 'window')} data-frozen={frozen || undefined} data-opened={rail.opened !== null || undefined} data-busy={(read.status === 'loading' && rows.length === 0) || undefined} style={size}>
      <span className="xe-rail-bar" aria-hidden="true" />
      <div className="xe-rail-view" ref={view} onPointerLeave={() => { if (canHover()) rail.leave(); }}>
        <div className="xe-rail-track" ref={track} {...press}>
          {rows.map((product, at) => (
            <RailGarment key={product.id} product={product} at={at} turned={rail.turned} away={rail.opened === at} showTag={boolProp(node.props, 'showTags', true)} mayBeNew={newest.has(product.id)}
              pictureRef={(element) => { pictures.current[at] = element; }} onOpen={rail.setOpened} onTurn={(where, slot) => { rail.turn(where); reveal(slot); }} />
          ))}
        </div>
      </div>
      {rows.length > 0 ? <div className="xe-rail-words"><RailCaption product={shown} restLine={textProp(node.props, 'restLine', '')} /></div> : null}
      {rows.length === 0 && read.status !== 'loading' ? <ProductListEmpty from={from} status={read.status} editing={editing} /> : null}
      {editing && read.status === 'failed' ? <CatalogNote>Products could not be loaded. Please try again.</CatalogNote> : null}
      {frozen && rows[0] ? <div className="xe-rail-design"><CatalogNote>Shown under a garment when a shopper presses it.</CatalogNote><ProductScope product={rows[0]}>{template}</ProductScope></div> : null}
      {!frozen && rail.opened !== null && rows[rail.opened] ? createPortal(
        <RailDetail rows={rows} at={rail.opened} sourceOf={(at) => pictures.current[at] ?? null} onMove={rail.setOpened} onClose={() => { const back = pictures.current[rail.opened ?? 0]?.closest('button'); rail.setOpened(null); requestAnimationFrame(() => back?.focus({ preventScroll: true })); }}>{template}</RailDetail>,
        dialogHost(view.current)
      ) : null}
    </div>
  );
};
